import React from 'react';
import { Plus, Trash2, Edit2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';

/**
 * Pestaña de gestión de usuarios
 */
const UsersTab = ({
  users,
  destinations,
  newUser,
  onNewUserChange,
  editingUser,
  onEditingUserChange,
  onAddUser,
  onEditUser,
  onUpdateUser,
  onDeleteUser,
  onCancelEdit,
  onDestinationToggle,
  onEditDestinationToggle,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onAddUser();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Create User */}
      <Card>
        <CardHeader>
          <CardTitle>Crear Nuevo Usuario</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => onNewUserChange({ ...newUser, email: e.target.value })}
                required
                placeholder="usuario@email.com"
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre Completo</Label>
              <Input
                value={newUser.full_name}
                onChange={(e) => onNewUserChange({ ...newUser, full_name: e.target.value })}
                required
                data-testid="new-user-fullname"
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => onNewUserChange({ ...newUser, password: e.target.value })}
                required
                data-testid="new-user-password"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_superuser"
                checked={newUser.is_superuser}
                onCheckedChange={(checked) =>
                  onNewUserChange({ ...newUser, is_superuser: checked })
                }
                data-testid="new-user-superuser"
              />
              <label htmlFor="is_superuser" className="text-sm font-medium">
                Superusuario
              </label>
            </div>
            <div className="space-y-2">
              <Label>Destinos Asignados</Label>
              <div className="grid grid-cols-2 gap-2">
                {destinations.map((dest) => (
                  <div key={dest.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`dest-${dest.id}`}
                      checked={newUser.assigned_destinations.includes(dest.name)}
                      onCheckedChange={() => onDestinationToggle(dest.name)}
                      data-testid={`new-user-dest-${dest.id}`}
                    />
                    <label htmlFor={`dest-${dest.id}`} className="text-sm">
                      {dest.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <Button type="submit" className="w-full" data-testid="create-user-btn">
              <Plus size={18} className="mr-1" />
              Crear Usuario
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Existing Users */}
      <Card>
        <CardHeader>
          <CardTitle>Usuarios Existentes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {users.map((u) => (
              <div
                key={u.id}
                className="p-4 border rounded-lg"
                data-testid={`user-${u.id}`}
              >
                {editingUser && editingUser.email === u.email ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nombre Completo</Label>
                      <Input
                        value={editingUser.full_name}
                        onChange={(e) =>
                          onEditingUserChange({ ...editingUser, full_name: e.target.value })
                        }
                        data-testid={`edit-user-fullname-${u.id}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editingUser.email}
                        onChange={(e) =>
                          onEditingUserChange({ ...editingUser, email: e.target.value })
                        }
                        data-testid={`edit-user-email-${u.id}`}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-is-superuser-${u.id}`}
                        checked={editingUser.is_superuser}
                        onCheckedChange={(checked) =>
                          onEditingUserChange({ ...editingUser, is_superuser: checked })
                        }
                        data-testid={`edit-user-superuser-${u.id}`}
                      />
                      <label htmlFor={`edit-is-superuser-${u.id}`} className="text-sm font-medium">
                        Superusuario
                      </label>
                    </div>
                    <div className="space-y-2">
                      <Label>Destinos Asignados</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {destinations.map((dest) => (
                          <div key={dest.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`edit-dest-${u.id}-${dest.id}`}
                              checked={editingUser.assigned_destinations.includes(dest.name)}
                              onCheckedChange={() => onEditDestinationToggle(dest.name)}
                              data-testid={`edit-user-dest-${u.id}-${dest.id}`}
                            />
                            <label htmlFor={`edit-dest-${u.id}-${dest.id}`} className="text-sm">
                              {dest.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={onUpdateUser}
                        size="sm"
                        data-testid={`save-user-${u.id}`}
                      >
                        <Save size={16} className="mr-1" />
                        Guardar
                      </Button>
                      <Button
                        variant="outline"
                        onClick={onCancelEdit}
                        size="sm"
                        data-testid={`cancel-edit-user-${u.id}`}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{u.full_name}</p>
                      <p className="text-sm text-gray-600">{u.email}</p>
                      {u.is_superuser && (
                        <Badge className="mt-2" variant="destructive">
                          Superusuario
                        </Badge>
                      )}
                      {u.assigned_destinations.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {u.assigned_destinations.map((dest, idx) => (
                            <Badge key={idx} variant="secondary">
                              {dest}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditUser(u)}
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDeleteUser(u.email, u.full_name)}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersTab;
